// /en/tax/$topic — EN detail. Phase P-TAX-DEEP-DIVE.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TaxTopicPage } from "@/components/TaxTopicPage";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/tax/$topic")({
  loader: async ({ params }) => {
    const { getTaxTopic } = await import("@/data/tax-topics");
    const topic = getTaxTopic(params.topic);
    if (!topic) throw notFound();
    return { topic };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const t = loaderData.topic;
    const url = `${SITE}/en/tax/${t.slug}`;
    const title = `${t.titleEn} — Thailand Tax Deep-Dive 2026 | NYC`;
    const desc = t.quickAnswerEn.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/en/tax/$topic", "en") }, { name: "description", content: ctaDesc(desc, "/en/tax/$topic", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/tax/$topic", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/tax/$topic", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" }, { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/tax/${t.slug}`, `/en/tax/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", ".quick-answer", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Article", "@id": `${url}#article`,
              headline: t.titleEn, description: desc, inLanguage: "en",
              author: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE },
              publisher: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
              datePublished: "2026-06-12", dateModified: "2026-06-12",
              mainEntityOfPage: url,
            }),
          },
          howToSchema({
            name: `How to: ${t.titleEn}`,
            description: t.taglineEn,
            inLanguage: "en",
            steps: t.rulesEn.map((r) => ({ name: r.name, text: r.text })),
          }),
          faqPageSchema(t.faqsEn),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Thai Tax", item: `${SITE}/en/tax` },
                { "@type": "ListItem", position: 3, name: t.titleEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">Topic not found</h1>
      <Link to="/en/tax" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">Back to Tax Hub</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">Error</h1>
      <Link to="/en/tax" className="mt-6 inline-block text-primary">Back</Link>
    </main>
  ),
  component: TaxTopicEn,
});

function TaxTopicEn() {
  const { topic } = Route.useLoaderData();
  return <TaxTopicPage topic={topic} locale="en" hubPath="/en/tax" relatedBaseTax="/en/tax" />;
}
